import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Badge } from '../ui/badge';
import { ClipboardList, FlaskConical, ArrowRight } from 'lucide-react';

const PROD_STATUS = {
  Released: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Draft: 'bg-slate-100 text-slate-700 border-slate-200',
  Obsolete: 'bg-neutral-100 text-neutral-500 border-neutral-200',
};
const fmt = (s) => (s ? new Date(s).toLocaleDateString() : '—');

const ProdNode = ({ n, current, onOpen }) => (
  <button type="button" onClick={onOpen} className={`w-full text-left rounded-md border px-3 py-2 transition-colors hover:border-orange-300 ${current ? 'border-orange-400 bg-orange-50/60 ring-1 ring-orange-200' : 'border-slate-200 bg-white'}`} data-testid={`lineage-prod-${n.id}`}>
    <div className="flex items-center gap-2 flex-wrap">
      <span className="font-semibold text-slate-800 font-mono">Rev {n.revision}</span>
      <Badge variant="outline" className={`text-[10px] border ${PROD_STATUS[n.status] || ''}`}>{n.status}</Badge>
      {n.in_rework && <Badge className="text-[10px] bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200">In R&D Rework</Badge>}
      {current && <span className="text-[10px] uppercase tracking-wide text-orange-600">You are here</span>}
    </div>
    <p className="text-[11px] text-slate-500 mt-0.5">
      Production · created {fmt(n.created_at)}{n.developed_by ? ` · by ${n.developed_by}` : ''} · {n.line_count} lines
      {n.rd_source_draft_bom_id && ' · came from R&D'}
    </p>
  </button>
);

const RDNode = ({ n, current, onOpen }) => (
  <button type="button" onClick={onOpen} className={`w-full text-left rounded-md border px-3 py-2 transition-colors hover:border-fuchsia-300 ${current ? 'border-fuchsia-400 bg-fuchsia-50 ring-1 ring-fuchsia-200' : 'border-fuchsia-100 bg-fuchsia-50/40'}`} data-testid={`lineage-rd-${n.id}`}>
    <div className="flex items-center gap-2 flex-wrap">
      <span className="font-semibold text-fuchsia-900">{n.draft_kind === 'rework' ? 'R&D Rework' : 'R&D Draft'}</span>
      <span className="text-xs text-fuchsia-700 truncate">in {n.project_name || 'project'}</span>
      <Badge variant="outline" className="text-[10px]">{n.status}</Badge>
      {current && <span className="text-[10px] uppercase tracking-wide text-fuchsia-600">You are here</span>}
    </div>
    <div className="flex items-center gap-1 flex-wrap mt-1">
      {(n.iterations || []).map(it => (
        <span key={it.iteration} title={`${it.saved_by_name || ''} · ${fmt(it.saved_at)} · ${it.line_count} lines`} className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${it.current ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'bg-white text-fuchsia-700 border-fuchsia-200'}`}>
          {it.revision}
        </span>
      ))}
    </div>
    <p className="text-[11px] text-slate-500 mt-1">
      {n.source_bom_revision ? `From prod rev ${n.source_bom_revision} · ` : ''}started {fmt(n.created_at)}{n.created_by_name ? ` by ${n.created_by_name}` : ''}
      {n.promoted_at && ` · promoted ${fmt(n.promoted_at)}`}
    </p>
  </button>
);

export const BomLineageTimeline = ({ bomId, draftId }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    const url = bomId ? `/boms/${bomId}/lineage` : `/rd/draft-boms/${draftId}/lineage`;
    api().get(url).then(r => setData(r.data)).catch(() => setData({ nodes: [] }));
  }, [api, bomId, draftId]);

  if (!data) return <p className="text-sm text-slate-500" data-testid="lineage-loading">Loading lineage…</p>;
  if (!data.nodes.length) return <p className="text-sm text-slate-500" data-testid="lineage-empty">This draft isn't linked to a production BOM yet. Promote it to start a lineage.</p>;

  return (
    <div className="space-y-1" data-testid="bom-lineage-timeline">
      {data.bom_variant_code && <p className="text-xs text-slate-500 mb-2">Full history of <span className="font-mono text-slate-700">{data.bom_variant_code}</span>, oldest first.</p>}
      <ol className="relative border-l-2 border-slate-200 ml-3 space-y-3">
        {data.nodes.map((n, i) => {
          const Icon = n.kind === 'production' ? ClipboardList : FlaskConical;
          const current = n.kind === 'production' ? n.id === data.current_id : n.id === data.current_draft_id;
          return (
            <li key={`${n.kind}-${n.id}`} className="ml-5 relative" data-testid={`lineage-node-${i}`}>
              <span className={`absolute -left-[31px] top-2 w-6 h-6 rounded-full flex items-center justify-center ring-4 ring-white ${n.kind === 'production' ? 'bg-orange-100 text-orange-600' : 'bg-fuchsia-100 text-fuchsia-600'}`}>
                <Icon className="w-3.5 h-3.5" />
              </span>
              {n.kind === 'production'
                ? <ProdNode n={n} current={current} onOpen={() => navigate(`/boms/view/${n.id}`)} />
                : <RDNode n={n} current={current} onOpen={() => navigate(`/rd/${n.project_id}?tab=boms`)} />}
              {n.kind === 'rd' && n.promoted_to_bom_id && (
                <p className="text-[11px] text-emerald-700 mt-1 inline-flex items-center gap-1"><ArrowRight className="w-3 h-3" />Promoted to production</p>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};
